/**
 * A receipt thumbnail that opens full size (BR-06, BR-07).
 *
 * BR-06 makes the photo mandatory so that whoever approves an entry can check
 * the figure against the paper. That only works if the approver can actually
 * see it — a "receipt attached" label is a promise, not evidence. Every
 * signed-in user can open one (§1.4); the fetch goes through the API client
 * because the upload route is authenticated (SEC-5).
 */

import { useEffect, useState } from 'react';

import { ProtectedImage, useObjectUrl, useUploadBlob } from '../../components/ProtectedImage.js';
import { useTranslation } from '../../i18n/useTranslation.js';

export function ReceiptViewer({ uploadId }: { uploadId: string }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('finance.receipt.open')}
        className="mt-2 block overflow-hidden rounded-lg border border-gray-200"
      >
        <ProtectedImage
          uploadId={uploadId}
          alt={t('finance.receipt.alt')}
          className="h-20 w-20 object-cover"
          fallback={
            <span className="flex h-20 w-20 items-center justify-center bg-gray-50 text-2xl" aria-hidden="true">
              📎
            </span>
          }
        />
      </button>

      {open && <ReceiptDialog uploadId={uploadId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ReceiptDialog({ uploadId, onClose }: { uploadId: string; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { blob, isError } = useUploadBlob(uploadId);
  const url = useObjectUrl(blob);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('finance.receipt.title')}
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      onClick={onClose}
    >
      <div className="flex justify-end gap-2 p-3">
        {url !== null && (
          // A new tab lets the phone's own viewer pinch-zoom a crumpled receipt.
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="min-h-touch rounded-lg bg-white/10 px-4 py-2 text-white"
          >
            {t('finance.receipt.newTab')}
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          className="min-h-touch rounded-lg bg-white/10 px-4 py-2 text-white"
          autoFocus
        >
          {t('finance.receipt.close')}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center p-3">
        {isError ? (
          <p role="alert" className="text-white">
            {t('finance.receipt.error')}
          </p>
        ) : url === null ? (
          <p role="status" className="text-white">
            {t('app.loading')}
          </p>
        ) : (
          <img
            src={url}
            alt={t('finance.receipt.alt')}
            className="max-h-full max-w-full object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        )}
      </div>
    </div>
  );
}
